import React, { memo, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Head from "next/head";
import ListAccordion from "./list-accordion";
import { useI18n } from "@/i18n/I18nProvider";

const WRAPPER_SX = { py: { xs: 6, md: 10 } };

const TITLE_SX = {
  fontSize: { xs: 30, sm: 36, md: 44, lg: 50 },
  fontWeight: 700,
  color: "#282781",
  textAlign: "center",
  mb: 3,
};

function DynamicFAQSection({
  title,
  items,
  schema,
  replaceValues = {},
  renderHTML = false,
  variantTitle,
  variantQ,
  key
}) {
  const { locale } = useI18n();

  const replacePlaceholders = useCallback(
    (text) => {
      if (!text) return "";
      let result = text;

      // Fast exit if no placeholders provided
      const entries = Object.entries(replaceValues);
      if (entries.length === 0) return result;

      for (const [key, value] of entries) {
        result = result.replaceAll(`{{${key}}}`, String(value ?? ""));
      }
      return result;
    },
    [replaceValues],
  );

  const resolvedTitle = useMemo(
    () => replacePlaceholders(title),
    [title, replacePlaceholders],
  );

  const schemaJSON = useMemo(() => {
    if (!schema) return "";

    // stringify once, then replace placeholders like {{key}}
    const json = JSON.stringify(schema);

    if (!replaceValues || Object.keys(replaceValues).length === 0) return json;

    return json.replace(/{{(.*?)}}/g, (_, key) =>
      String(replaceValues[key] ?? ""),
    );
  }, [schema, replaceValues]);
  const faqs = React.useMemo(() => {
    if (!items) return [];

    return items.map((item) => {
      const hasDirectQA = item?.q !== undefined || item?.a !== undefined;

      return {
        q: hasDirectQA
          ? item.q
          : locale === "ar"
            ? item.question_ar
            : item.question_en,

        a: hasDirectQA
          ? item.a
          : locale === "ar"
            ? item.answer_ar
            : item.answer_en,
      };
    });
  }, [items, locale]);
  return (
    <>
      {schema ? (
        <Head>
          <script
            type="application/ld+json"
            // safe: schemaJSON is created from object + placeholder substitution
            dangerouslySetInnerHTML={{ __html: schemaJSON }}
          />
        </Head>
      ) : null}

      <Box sx={WRAPPER_SX}>
        <Container maxWidth="md">
          {title ? (
            <Typography
              sx={TITLE_SX}
              variant={variantTitle ?? "h3"}
              data-aos="fade-down"
              data-aos-delay="500"
            >
              {resolvedTitle}
            </Typography>
          ) : null}

          <ListAccordion
            items={faqs}
            renderHTML={renderHTML}
            replaceValues={replaceValues}
            variant={variantQ}
          />
        </Container>
      </Box>
    </>
  );
}

export default memo(DynamicFAQSection);
